import ArrowDownward from '@mui/icons-material/ArrowDownward'
import ArrowDropDown from '@mui/icons-material/ArrowDropDown'
import ArrowRight from '@mui/icons-material/ArrowRight'
import ArrowUpward from '@mui/icons-material/ArrowUpward'
import LinkIcon from '@mui/icons-material/Link'
import LinkOff from '@mui/icons-material/LinkOff'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'
import { Link as ReactRouterLink } from 'react-router'
import { useSiblingTabPath } from '~/shared/hooks/useRouteBasePath'
import { Person } from '~/shared/types'
import { EmptyState, SectionCard, StatusPill, TABLE_HEADER_CELL_SX, type Tone } from '~/shared/ui'
import { asDate, getDeterminationDisplay } from './helpers'
import PriorAuthorizationItem from './PriorAuthorizationItem'
import { usePriorAuthorizations } from './usePriorAuthorizations'

interface PriorAuthorizationListProps {
  person: Person
}

// Tone keyed on the raw determination; the pill label keeps the exact
// getDeterminationDisplay string (including the status suffix).
const DETERMINATION_TONE: Record<string, Tone> = {
  Approved: 'success',
  Denied: 'error',
}

/**
 * Design-refresh Prior Authorization tab (shared by patient and person
 * modes): the legacy list's data, sorting, expandable details, and case
 * links verbatim, re-rendered as a SectionCard table with the determination
 * as a toned status pill. The expanded details reuse PriorAuthorizationItem.
 */
const PriorAuthorizationList = (props: PriorAuthorizationListProps) => {
  const todosPath = useSiblingTabPath('todos')
  const { person } = props
  const [sortDesc, setSortDesc] = useState(true)
  const [open, setOpen] = useState<number | null>(null)

  const { data: priorAuthorizations = [] } = usePriorAuthorizations(person.id)

  const sortedPriorAuthorizations = React.useMemo(() => {
    return [...priorAuthorizations].sort((a, b) => {
      const dateA = new Date(a.vendor_create_date || a.effective_from || 0)
      const dateB = new Date(b.vendor_create_date || b.effective_from || 0)
      return sortDesc ? dateB.getTime() - dateA.getTime() : dateA.getTime() - dateB.getTime()
    })
  }, [priorAuthorizations, sortDesc])

  return (
    <Box sx={{ px: 3, pt: 1.5, pb: 5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 1,
        }}
      >
        <Typography sx={{ fontSize: '1.25rem', color: 'text.secondary' }}>
          {sortedPriorAuthorizations.length}{' '}
          {sortedPriorAuthorizations.length === 1 ? 'record' : 'records'}
        </Typography>
        <Tooltip title="Sort by created date" placement="top">
          <IconButton
            size="small"
            aria-label="Sort by created date"
            onClick={() => {
              setSortDesc(s => !s)
            }}
          >
            {sortDesc ? <ArrowUpward /> : <ArrowDownward />}
          </IconButton>
        </Tooltip>
      </Box>

      {sortedPriorAuthorizations.length === 0 ? (
        <EmptyState icon={<FactCheckOutlinedIcon />} title="No prior authorizations" />
      ) : (
        <SectionCard disablePadding>
          <Table aria-label="Prior authorizations" sx={{ 'td, th': { borderColor: 'divider' } }}>
            <TableHead>
              <TableRow>
                <TableCell width="25%" sx={TABLE_HEADER_CELL_SX}>
                  Determination
                </TableCell>
                <TableCell width="20%" sx={TABLE_HEADER_CELL_SX}>
                  Created Date
                </TableCell>
                <TableCell width="30%" sx={TABLE_HEADER_CELL_SX}>
                  Details
                </TableCell>
                <TableCell width="25%" sx={TABLE_HEADER_CELL_SX}>
                  Cases
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedPriorAuthorizations.map((priorAuth, index) => {
                const isLast = index === sortedPriorAuthorizations.length - 1
                return (
                  <React.Fragment key={priorAuth.pk}>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ borderBottom: 0 }}>
                        <StatusPill
                          tone={
                            priorAuth.determination
                              ? (DETERMINATION_TONE[priorAuth.determination] ?? 'neutral')
                              : 'warning'
                          }
                          label={getDeterminationDisplay(priorAuth)}
                        />
                      </TableCell>
                      <TableCell align="left" sx={{ borderBottom: 0, fontSize: '1.3rem' }}>
                        {asDate(priorAuth.vendor_create_date || priorAuth.effective_from)}
                      </TableCell>
                      <TableCell sx={{ borderBottom: 0 }}>
                        <Button
                          onClick={() => setOpen(open === priorAuth.pk ? null : priorAuth.pk)}
                          startIcon={open === priorAuth.pk ? <ArrowDropDown /> : <ArrowRight />}
                        >
                          View Details
                        </Button>
                      </TableCell>
                      <TableCell sx={{ borderBottom: 0 }}>
                        {priorAuth.steerage_case_ids.length > 0 ? (
                          <Box sx={{ display: 'inline-flex', flexDirection: 'column', gap: 0.5 }}>
                            {priorAuth.steerage_case_ids.map(caseId => (
                              <Tooltip key={caseId} title={`Go to Case ${caseId}`}>
                                <Button
                                  component={ReactRouterLink}
                                  to={`${todosPath}?caseId=${caseId}`}
                                  startIcon={<LinkIcon />}
                                >
                                  Case #{caseId}
                                </Button>
                              </Tooltip>
                            ))}
                          </Box>
                        ) : (
                          <Tooltip title="No cases linked">
                            <LinkOff fontSize="small" sx={{ ml: 0.5, color: 'grey.400' }} />
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={4}
                        sx={{ borderBottom: isLast ? 0 : undefined }}
                      >
                        <Collapse in={open === priorAuth.pk} timeout="auto" unmountOnExit>
                          <Box sx={{ px: 1, pb: 2 }}>
                            <PriorAuthorizationItem priorAuth={priorAuth} />
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                )
              })}
            </TableBody>
          </Table>
        </SectionCard>
      )}
    </Box>
  )
}

export default PriorAuthorizationList
